import { Fragment } from 'react';

import { Translate } from '@spazious/dollhouse-i18n';
import { styled } from '@spazious/dollhouse-theme';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogProps,
  Flex,
  Typography,
} from '@spazious/dollhouse-uikit';

import { getShortcutsByCategory, resolveKeys } from '../../shortcuts/index.js';
import { isMacPlatform } from '../../utils/index.js';
import { CloseButton } from '../CloseButton/index.js';

const Key = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  padding: calc(var(--spacing-gap) / 2) var(--spacing-gap);
  font-family: inherit;
  font-size: 0.8125rem;
  line-height: 1;
  background-color: var(--spz-sys-color-surface-container-highest);
  border: 1px solid var(--spz-sys-color-surface-container);
  border-radius: calc(var(--shape-rounded-radius) / 2);
`;

const Separator = styled.span`
  color: var(--spz-sys-color-on-surface-variant);
`;

/**
 * Two-column layout for a category's shortcuts: descriptions in the first
 * column, key combos aligned in a single column on the right (`auto` sizes to
 * the widest combo so every row's keys line up).
 */
const ShortcutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--spacing-gap) calc(var(--spacing-gap) * 2);
`;

const KeyCombo: React.FC<{ keys: string[] }> = ({ keys }) => (
  <Flex alignItems="center" gap="calc(var(--spacing-gap) / 2)">
    {keys.map((key, index) => (
      <Fragment key={index}>
        {index > 0 && <Separator>+</Separator>}
        <Key>{key}</Key>
      </Fragment>
    ))}
  </Flex>
);

export interface KeyboardShortcutsDialogProps extends DialogProps {}

export const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  onClose,
  ...restProps
}) => {
  const isMac = isMacPlatform();
  const groups = getShortcutsByCategory();

  return (
    <Dialog fullWidth maxWidth="560px" onClose={onClose} {...restProps}>
      <DialogHeader>
        <Typography size="medium" variant="headline">
          <Translate>Keyboard shortcuts</Translate>
        </Typography>
        <CloseButton onClick={(evt) => onClose?.(evt, 'backdropClick')} />
      </DialogHeader>
      <DialogContent>
        <Flex column grow gap="calc(var(--spacing-gap) * 3)">
          {groups.map(({ category, shortcuts }) => (
            <Flex key={category} column gap="var(--spacing-gap)">
              <Typography size="small" variant="label">
                <Translate>{category}</Translate>
              </Typography>
              <ShortcutGrid>
                {shortcuts.map((shortcut) => (
                  <Fragment key={shortcut.id}>
                    <Typography>
                      <Translate>{shortcut.description}</Translate>
                      {shortcut.hold && (
                        <Typography size="small" variant="label">
                          {' '}
                          (<Translate>hold</Translate>)
                        </Typography>
                      )}
                    </Typography>
                    <Flex alignItems="center" gap="var(--spacing-gap)" justifyContent="flex-end">
                      <KeyCombo keys={resolveKeys(shortcut.keys, isMac)} />
                      {shortcut.altKeys && (
                        <>
                          <Separator>
                            <Translate>or</Translate>
                          </Separator>
                          <KeyCombo keys={resolveKeys(shortcut.altKeys, isMac)} />
                        </>
                      )}
                    </Flex>
                  </Fragment>
                ))}
              </ShortcutGrid>
            </Flex>
          ))}
        </Flex>
      </DialogContent>
    </Dialog>
  );
};
